import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import './SignUp.css';
import { signup } from '../../services/httpService';
import logo from '../../assets/images/logo.png';
import loginBg from '../../assets/images/login-bg.jpg';

function SignUp() {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showRePassword, setShowRePassword] = useState(false);

    // Yup validation schema
    const validationSchema = Yup.object().shape({
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        email: Yup.string()
            .email('Invalid email format')
            .required('Email is required'),
        organizationName: Yup.string().required('Organization name is required'),
        password: Yup.string()
            .required('Password is required')
            .min(8, 'Password must be at least 8 characters'),
        rePassword: Yup.string()
            .oneOf([Yup.ref('password')], 'Passwords must match')
            .required('Please re-enter your password'),
    });

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: yupResolver(validationSchema)
    });

    const onSubmit = async (data) => {
        const payload = {
            company_name: data.organizationName,
            confirmPassword: data.rePassword,
            firstName: data.firstName,
            lastName: data.lastName,
            name: `${data.firstName} ${data.lastName}`,
            email: data.email,
            password: data.password,
            region: "",
        };

        try {
            const response = await signup(payload, reset);
            if (response && response.success) {
                navigate('/signup-success');
            }
        } catch (error) {
            console.error("Error submitting staff details:", error);
        }
    };

    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleRePasswordVisibility = () => setShowRePassword(!showRePassword);

    return (
        <div className="signup-page-container">
            <div className="signup-header" style={{ backgroundImage: `url(${loginBg})` }}>
            </div>

            <div className="signup-card-wrapper">
                <div className="header-content">
                    <img src={logo} alt="brainyclock" className="logo-white" />
                </div>
                <div className="signup-card">
                    <h2 className="card-title">Create you account</h2>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-grid">
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="First Name"
                                    {...register('firstName')}
                                    className={errors.firstName ? 'input-error' : ''}
                                />
                                {errors.firstName && <p className="error-text">{errors.firstName.message}</p>}
                            </div>

                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Last Name"
                                    {...register('lastName')}
                                    className={errors.lastName ? 'input-error' : ''}
                                />
                                {errors.lastName && <p className="error-text">{errors.lastName.message}</p>}
                            </div>

                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Organization Name"
                                    {...register('organizationName')}
                                    className={errors.organizationName ? 'input-error' : ''}
                                />
                                {errors.organizationName && <p className="error-text">{errors.organizationName.message}</p>}
                            </div>

                            <div className="form-group">
                                <input
                                    type="email"
                                    placeholder="Email"
                                    {...register('email')}
                                    className={errors.email ? 'input-error' : ''}
                                />
                                {errors.email && <p className="error-text">{errors.email.message}</p>}
                            </div>

                            <div className="form-group password-group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Password"
                                    {...register('password')}
                                    className={errors.password ? 'input-error' : ''}
                                />
                                <span className="password-toggle" onClick={togglePasswordVisibility}>
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                    )}
                                </span>
                                {errors.password && <p className="error-text">{errors.password.message}</p>}
                            </div>

                            <div className="form-group password-group">
                                <input
                                    type={showRePassword ? "text" : "password"}
                                    placeholder="Re-enter password"
                                    {...register('rePassword')}
                                    className={errors.rePassword ? 'input-error' : ''}
                                />
                                <span className="password-toggle" onClick={toggleRePasswordVisibility}>
                                    {showRePassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                    )}
                                </span>
                                {errors.rePassword && <p className="error-text">{errors.rePassword.message}</p>}
                            </div>
                        </div>

                        <p className="password-hint">
                            A strong password is 8-16 characters long and includes at least one number, one letter and a special character.
                        </p>

                        <div className="form-actions">
                            <button type="button" className="btn-cancel" onClick={() => navigate('/')}>Cancel</button>
                            <button type="submit" className="btn-submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Signing Up...' : 'Sign Me Up!'}
                            </button>
                        </div>
                    </form>

                    <div className="card-footer">
                        Already have an account? <Link to="#" onClick={(e) => { e.preventDefault(); window.open("https://portal.brainyclock.com", "_blank"); }}>Sign in</Link>
                    </div>
                </div>
                <div className="signup-footer-text">
                    Attendance, simplified.
                </div>
            </div>

        </div>
    );
}

export default SignUp;
